/**
 * One seller's position, opened from a row.
 *
 * The drawer's whole job is to answer "why is this number what it is" without
 * a second screen. So it shows the arithmetic — gross, each deduction, net —
 * and then the stage breakdown, which is what explains a row that reads
 * "awaiting COD remittance" while most of the seller's orders are settled.
 *
 * TCS and TDS stay on separate lines here. They are different taxes with
 * different filings, and a single "tax" line would be a figure that appears on
 * no return.
 *
 * The payout section is separated by a rule and titled as a separate thing,
 * because it is one: `PayoutRequest` carries no reference to the settlement
 * rows above it. The drawer never says this payout covers this money.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { Snowflake, X } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { SETTLEMENT_STATUS_LABEL, formatDate, formatDateTime, formatPaise } from '../../lib/money';

import type { SellerPayoutRow } from './SellerPayouts';
import { StagePill } from './StagePill';
import { PAYOUT_TONE, SETTLEMENT_TONE } from './tones';

function Line({
  label,
  value,
  negative,
  strong,
  hint,
}: {
  label: string;
  value: number;
  negative?: boolean;
  strong?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span
        className={`text-[13px] ${strong ? 'text-ink font-bold' : 'text-ink-muted font-medium'}`}
      >
        {label}
        {hint && <span className="text-ink-muted/70 ml-1 text-[11.5px]">{hint}</span>}
      </span>
      <span
        className={`tabular-nums ${
          strong ? 'text-ink text-[16px] font-bold' : 'text-ink text-[13px] font-semibold'
        }`}
      >
        {negative ? '−' : ''}
        {formatPaise(value)}
      </span>
    </div>
  );
}

export function PayoutDetailDrawer({
  row,
  onClose,
}: {
  row: SellerPayoutRow | null;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!row) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [row, onClose]);

  return (
    <AnimatePresence>
      {row && (
        <motion.div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="payout-drawer-title"
            // Full width on a phone, a panel on a desktop.
            className="bg-surface flex h-full w-full max-w-lg flex-col shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="border-border/60 flex items-start justify-between gap-4 border-b px-5 py-4">
              <div className="min-w-0">
                <h2 id="payout-drawer-title" className="text-ink truncate text-[17px] font-bold">
                  {row.storeName}
                </h2>
                {row.email && (
                  <p className="text-ink-muted truncate text-[12.5px] font-medium">{row.email}</p>
                )}
                <p className="text-ink-muted/70 mt-0.5 font-mono text-[11px]">{row.vendorId}</p>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
              {row.payoutFrozen && (
                <p className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-red-700">
                  <Snowflake className="mt-px h-4 w-4 shrink-0" aria-hidden />
                  Payouts are frozen for this seller. Settlement continues; no transfer will be
                  released until an admin lifts the freeze.
                </p>
              )}

              {/* ── 1. Settlement position ───────────────────────────── */}
              <section>
                <h3 className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Settlement position
                </h3>
                <p className="text-ink mt-2 text-[28px] font-bold tabular-nums leading-none">
                  {formatPaise(row.netPayablePaise)}
                </p>
                <p className="text-ink-muted mt-1 text-[12.5px] font-medium">
                  net across {row.ordersCount} {row.ordersCount === 1 ? 'order' : 'orders'}
                </p>

                <div className="border-border/60 mt-4 divide-y divide-dashed">
                  <Line label="Gross sales" value={row.grossPaise} />
                  <Line label="Platform commission" value={row.commissionPaise} negative />
                  <Line label="TCS" value={row.tcsPaise} negative hint="GST u/s 52" />
                  <Line label="TDS" value={row.tdsPaise} negative hint="income tax u/s 194-O" />
                  <Line
                    label="Courier freight"
                    value={row.shippingFeePaise ?? 0}
                    negative
                    hint="forward, and return on RTO"
                  />
                  <Line label="Return reserve held" value={row.reserveHeldPaise} negative />
                  <Line label="Net payable" value={row.netPayablePaise} strong />
                </div>
              </section>

              {/* ── 2. Status breakdown ──────────────────────────────── */}
              <section>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Settlement status
                  </h3>
                  <StagePill tone={SETTLEMENT_TONE[row.status] ?? 'slate'}>
                    {SETTLEMENT_STATUS_LABEL[row.status] ?? row.status}
                  </StagePill>
                </div>
                <p className="text-ink-muted mt-1.5 text-[12.5px] font-medium leading-relaxed">
                  The headline stage is the earliest one present — the one holding this
                  seller&apos;s money up, not the one furthest along.
                </p>
                <ul className="mt-3 space-y-1.5">
                  {row.statusBreakdown.map((b) => (
                    <li
                      key={b.status}
                      className="border-border/50 bg-surface-subtle/40 flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                    >
                      <span className="text-ink text-[12.5px] font-semibold">
                        {SETTLEMENT_STATUS_LABEL[b.status] ?? b.status}
                      </span>
                      <span className="text-ink-muted text-[12px] font-medium tabular-nums">
                        {b.count} {b.count === 1 ? 'order' : 'orders'} ·{' '}
                        {formatPaise(b.netPayablePaise)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-ink-muted mt-2 text-[12px] font-medium">
                  Last settled: {formatDate(row.lastSettledAt)}
                </p>
              </section>

              {/* ── 3. Latest payout request ─────────────────────────── */}
              <section className="border-border/60 border-t pt-5">
                <h3 className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  Latest payout request
                </h3>
                <p className="text-ink-muted mt-1.5 text-[12.5px] font-medium leading-relaxed">
                  Reported separately on purpose: a payout request carries no reference to the
                  settlement rows above, so this amount is not a total of them.
                </p>

                {row.latestPayout ? (
                  <div className="border-border/60 mt-3 rounded-xl border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-ink text-[18px] font-bold tabular-nums">
                        {formatPaise(row.latestPayout.amountPaise)}
                      </span>
                      <StagePill tone={PAYOUT_TONE[row.latestPayout.status] ?? 'slate'}>
                        {row.latestPayout.status}
                      </StagePill>
                    </div>
                    <ol className="mt-3 space-y-2">
                      {[
                        { label: 'Requested', at: row.latestPayout.requestedAt },
                        { label: 'Approved', at: row.latestPayout.approvedAt },
                        { label: 'Released to bank', at: row.latestPayout.releasedAt },
                      ].map((step) => (
                        <li key={step.label} className="flex items-center gap-2.5">
                          <span
                            className={`grid h-4 w-4 shrink-0 place-items-center rounded-full text-[9px] font-black text-white ${
                              step.at ? 'bg-emerald-500' : 'bg-border-light'
                            }`}
                            aria-hidden
                          >
                            {step.at ? '✓' : ''}
                          </span>
                          <span
                            className={`text-[12.5px] ${
                              step.at ? 'text-ink font-semibold' : 'text-ink-muted/70 font-medium'
                            }`}
                          >
                            {step.label}
                          </span>
                          <span className="text-ink-muted ml-auto text-[11.5px] tabular-nums">
                            {/* Only real timestamps; a step with no date shows none. */}
                            {step.at ? formatDateTime(step.at) : '—'}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  <p className="border-border/60 text-ink-muted mt-3 rounded-xl border border-dashed px-4 py-5 text-center text-[12.5px] font-medium">
                    This seller has not requested a payout yet.
                  </p>
                )}
              </section>

              <p className="text-ink-muted/80 text-[11.5px] font-medium leading-relaxed">
                Approving, rejecting or releasing a payout is done from the seller lookup below the
                table, where the individual payout requests are listed.
              </p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
